import React from "react";
import { GBButton } from "./CTAButtons";
import { container_classname } from "./constants";

const ACCENT = "#3364FA";

export interface EventRegistrationCta {
    label: string;
    href: string;
}

export interface EventRegistrationTier {
    eyebrow: string;
    price: string;
    subtitle: string;
    badge?: string;
    cta: EventRegistrationCta;
    features: string[];
    isDisabled?: boolean;
}

export interface EventRegistrationData {
    title: string;
    paragraphs: string[];
    highlightLine: string;
    features: string[];
    earlyCard: EventRegistrationTier;
    standardCard: EventRegistrationTier;
    footnote: string;
}

export interface EventRegistrationSectionProps {
    data: EventRegistrationData;
}

type RegistrationCardVariant = "featured" | "standard";

interface RegistrationCardProps {
    tier: EventRegistrationTier;
    variant: RegistrationCardVariant;
}

const RegistrationCard: React.FC<RegistrationCardProps> = ({ tier, variant }) => {
    const isFeatured = variant === "featured";
    const isDisabled = tier?.isDisabled || false;
    const cardStyle: React.CSSProperties = isFeatured
        ? {
              border: "2px solid rgba(49, 130, 206, 1)",
              boxShadow: "0px 12px 28px -18px rgba(16, 24, 40, 0.28)",
              backgroundColor: "white",
              color: "rgba(0, 0, 0, 1)",
          }
        : {
              backgroundColor: "rgba(237, 242, 247, 1)",
              color: "rgba(160, 174, 192, 1)",
          };

    return (
        <div
            className="relative flex flex-col justify-between rounded-2xl p-6 text-center  md:p-8"
            style={{
                minHeight: "290px",
                ...cardStyle,
            }}
            aria-disabled={isDisabled}
        >
            <div className="flex flex-col items-center justify-end h-full">
                {isFeatured && tier.badge && (
                    <span className="mb-4 inline-flex rounded-full px-2 pt-1 font-bold text-white uppercase" style={{ backgroundColor: ACCENT, fontSize: "10px" }}>
                        {tier.badge}
                    </span>
                )}

                <p className={`text-xs font-semibold tracking-[0.12em] uppercase ${isDisabled ? "text-slate-400" : "text-slate-700"}`}>{tier.eyebrow}</p>
                <p className={`mt-3 text-5xl font-bold leading-none md:text-6xl ${isFeatured ? "text-black" : "text-slate-400"}`}>{tier.price}</p>
                {tier.subtitle && <p className={`mt-3 text-sm leading-6 ${isDisabled ? "text-slate-400" : "text-slate-600"}`}>{tier.subtitle}</p>}
            </div>

            <GBButton
                className="mt-8 w-full items-center rounded-xl text-center"
                variant="primary-light"
                size="lg"
                href={isDisabled ? "#" : tier.cta.href}
                style={{
                    backgroundColor: isDisabled ? "#64748B" : ACCENT,
                    color: isDisabled ? "rgba(255, 255, 255, 0.72)" : "#FFFFFF",
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    pointerEvents: isDisabled ? "none" : "auto",
                    opacity: isDisabled ? 0.95 : 1,
                }}
                aria-disabled={isDisabled}
                tabIndex={isDisabled ? -1 : undefined}
            >
                {tier.cta.label}
            </GBButton>
        </div>
    );
};

const EventRegistrationSection: React.FC<EventRegistrationSectionProps> = ({ data }) => {
    const { title, paragraphs, highlightLine, earlyCard, standardCard } = data;

    const tierCards: Array<{ tier: EventRegistrationTier; variant: RegistrationCardVariant }> = [
        { tier: earlyCard, variant: "featured" },
        { tier: standardCard, variant: "standard" },
    ];

    return (
        <section className="lg:py-20 py-14 font-inter">
            <div className={container_classname}>
                <div className="mx-auto max-w-7xl">
                    <h2 className="md:text-5xl text-3xl font-semibold text-gray-900 md:mb-6 mb-4" style={{ fontFamily: "Degular Display" }}>
                        {title}
                    </h2>
                    <div className="space-y-4 text-base text-gray-600 leading-relaxed">
                        {paragraphs.map((p, i) => (
                            <p key={i}>{p}</p>
                        ))}
                    </div>
                    <p className="mt-8 text-xl font-semibold" style={{ color: ACCENT }}>
                        {highlightLine}
                    </p>

                    <div className="mt-14 grid gap-8 md:grid-cols-2 md:items-stretch">
                        {tierCards.map(({ tier, variant }) => (
                            <RegistrationCard key={variant} tier={tier} variant={variant} />
                        ))}
                    </div>

                    <p className="mt-5 text-center text-sm text-gray-500 mx-auto leading-relaxed font-inter">
                        <b className="font-bold">Confirmation sent via WhatsApp within 24 hours.</b> Max 2 attendees per organization. Free tickets close automatically once 100 are
                        claimed.
                    </p>
                </div>
            </div>
        </section>
    );
};

export default EventRegistrationSection;
